/**
 * @fileoverview Capa de Datos (Data Layer - DL) para la dinámica 'Memorama Individual' (Tile ID 51).
 * Proporciona llamadas HTTP fetch nativas desacopladas para interactuar con los endpoints REST del backend.
 * 
 * @module utilities/DL/MemoryIndividual
 */

const API_URL = process.env.REACT_APP_API_URL;

// =============================================================================
// ENDPOINTS PÚBLICOS DEL AFICIONADO
// =============================================================================

/**
 * Consulta la configuración pública activa y las cartas del memorama.
 * GET /api/MemoryIndividual/config
 * 
 * @async
 * @function GetPublicConfig
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetPublicConfig = async () => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/config`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al obtener la configuración de la dinámica.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData.data !== undefined ? resData.data : resData };
    } catch (error) {
        console.error("Error en GetPublicConfig (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al consultar configuración.' };
    }
};

/**
 * Registra a un aficionado, implementando la política de intento único y reanudación de sesión.
 * POST /api/MemoryIndividual/register
 * 
 * @async
 * @function RegisterParticipant
 * @param {Object} payload - { name, email, phone, acceptedTerms, source }
 * @returns {Promise<{success: boolean, code?: string, message?: string, participantId?: number, sessionToken?: string, previousTime?: string, formattedTime?: string}>}
 */
export const RegisterParticipant = async (payload) => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al procesar el registro de participación.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData };
    } catch (error) {
        console.error("Error en RegisterParticipant (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al registrar aficionado.' };
    }
};

/**
 * Inicia formalmente la sesión de juego en el servidor registrando la marca de tiempo de partida.
 * POST /api/MemoryIndividual/game-start
 * 
 * @async
 * @function StartGameSession
 * @param {Object} payload - { participantId, sessionToken }
 * @returns {Promise<{success: boolean, message?: string, serverStartTimeUtc?: string}>}
 */
export const StartGameSession = async (payload) => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/game-start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al iniciar la sesión de juego en el servidor.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData };
    } catch (error) {
        console.error("Error en StartGameSession (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al iniciar partida.' };
    }
};

/**
 * Envía y valida autoritativamente el resultado de la partida con motor anti-cheat.
 * POST /api/MemoryIndividual/submit-result
 * 
 * @async
 * @function SubmitResult
 * @param {Object} payload - { participantId, sessionToken, clientTimeMs, formattedTime, attempts }
 * @returns {Promise<{success: boolean, status?: string, message?: string, timeElapsedMs?: number, formattedTime?: string}>}
 */
export const SubmitResult = async (payload) => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/submit-result`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al registrar el resultado de la partida.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData };
    } catch (error) {
        console.error("Error en SubmitResult (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al enviar resultados.' };
    }
};

/**
 * Consulta la tabla oficial de clasificación (leaderboard) aplicando criterios de desempate.
 * GET /api/MemoryIndividual/leaderboard?top=50
 * 
 * @async
 * @function GetLeaderboard
 * @param {number} [top=50] - Cantidad de registros a consultar.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>}
 */
export const GetLeaderboard = async (top = 50) => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/leaderboard?top=${top}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al obtener la tabla de líderes.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData.data !== undefined ? resData.data : resData };
    } catch (error) {
        console.error("Error en GetLeaderboard (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al consultar leaderboard.' };
    }
};

/**
 * Registra eventos de telemetría del embudo de conversión (anonimizados sin PII).
 * POST /api/MemoryIndividual/track-metric
 * 
 * @async
 * @function TrackMetric
 * @param {string} eventName - Nombre del evento (ej: 'page_view', 'form_complete', 'game_start', 'game_complete').
 * @param {string|null} [sessionId=null] - Identificador único de sesión.
 * @param {string|null} [source=null] - Origen del tráfico ('direct', 'qr_screen', etc.).
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const TrackMetric = async (eventName, sessionId = null, source = null) => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/track-metric`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ eventName, sessionId, source })
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: resData.message || 'Error al registrar métrica.' };
        }
        return resData.success !== undefined ? resData : { success: true };
    } catch (error) {
        console.warn("Fallo no bloqueante en TrackMetric (DL):", error);
        return { success: false, message: 'Error de conexión en telemetría.' };
    }
};

// =============================================================================
// ENDPOINTS ADMINISTRATIVOS (TILE 51)
// =============================================================================

/**
 * Consulta la lista completa de temas visuales configurados en el panel de administración.
 * GET /api/MemoryIndividual/admin/themes
 * 
 * @async
 * @function GetAdminThemes
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>}
 */
export const GetAdminThemes = async () => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/admin/themes`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al obtener temas de Memorama Individual.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData.data !== undefined ? resData.data : resData };
    } catch (error) {
        console.error("Error en GetAdminThemes (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al consultar temas.' };
    }
};

/**
 * Obtiene el detalle de un tema específico por su ID.
 * GET /api/MemoryIndividual/admin/themes/{id}
 * 
 * @async
 * @function GetAdminThemeById
 * @param {number|string} id - ID del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetAdminThemeById = async (id) => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/admin/themes/${id}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al obtener el tema seleccionado.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData.data !== undefined ? resData.data : resData };
    } catch (error) {
        console.error("Error en GetAdminThemeById (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al consultar detalle del tema.' };
    }
};

/**
 * Guarda (crea o actualiza) un tema visual de Memorama Individual.
 * POST /api/MemoryIndividual/admin/themes
 * 
 * @async
 * @function SaveAdminTheme
 * @param {Object} payload - { id, name, cardBackImage, cards, isActive }
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const SaveAdminTheme = async (payload) => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/admin/themes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al guardar tema visual.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData.data !== undefined ? resData.data : resData };
    } catch (error) {
        console.error("Error en SaveAdminTheme (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al guardar tema.' };
    }
};

/**
 * Elimina un tema visual del sistema.
 * DELETE /api/MemoryIndividual/admin/themes/{id}
 * 
 * @async
 * @function DeleteAdminTheme
 * @param {number|string} id - ID del tema a eliminar.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const DeleteAdminTheme = async (id) => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/admin/themes/${id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al eliminar tema visual.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData };
    } catch (error) {
        console.error("Error en DeleteAdminTheme (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al eliminar tema.' };
    }
};

/**
 * Activa un tema visual en 1 clic como el tema activo oficial del juego.
 * POST /api/MemoryIndividual/admin/themes/{id}/select
 * 
 * @async
 * @function SelectActiveTheme
 * @param {number|string} id - ID del tema a activar.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const SelectActiveTheme = async (id) => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/admin/themes/${id}/select`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al activar tema oficial.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, message: 'Tema activado exitosamente.' };
    } catch (error) {
        console.error("Error en SelectActiveTheme (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al activar tema.' };
    }
};

/**
 * Sube y optimiza con SkiaSharp una imagen para carta o reverso (conversión a WebP máx 600px).
 * POST /api/MemoryIndividual/admin/upload-card
 * 
 * @async
 * @function UploadCardImage
 * @param {FormData} formData - FormData con el campo 'file'.
 * @returns {Promise<{success: boolean, message?: string, imageUrl?: string, width?: number, height?: number, fileSizeBytes?: number}>}
 */
export const UploadCardImage = async (formData) => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/admin/upload-card`, {
            method: 'POST',
            // No incluir Content-Type explícito para que el navegador genere el multipart boundary correcto
            body: formData
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al procesar la imagen de la carta.'
            };
        }
        return resData;
    } catch (error) {
        console.error("Error en UploadCardImage (DL):", error);
        return { success: false, message: 'Error de conexión con el servidor al subir imagen.' };
    }
};

/**
 * Proyecta la portada con código QR de Memorama Individual en la pantalla gigante vía WebSocket (Tile ID 51).
 * POST /api/MemoryIndividual/admin/send-qr
 * 
 * @async
 * @function SendQROverWebSocket
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const SendQROverWebSocket = async () => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/admin/send-qr`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al proyectar QR de Memorama en pantalla.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, message: 'Portada de Memorama Individual proyectada en pantalla gigante' };
    } catch (error) {
        console.error("Error en SendQROverWebSocket (DL):", error);
        return { success: false, message: 'Error de conexión al enviar QR a pantalla.' };
    }
};

/**
 * Obtiene las métricas consolidadas del embudo de conversión y padrón para el panel de administración.
 * GET /api/MemoryIndividual/admin/stats
 * 
 * @async
 * @function GetAdminStats
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetAdminStats = async () => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/admin/stats`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al obtener estadísticas administrativas.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData.data !== undefined ? resData.data : resData };
    } catch (error) {
        console.error("Error en GetAdminStats (DL):", error);
        return { success: false, message: 'Error de conexión al obtener estadísticas.' };
    }
};

/**
 * Obtiene el padrón de participantes paginado y filtrado para el panel administrativo.
 * GET /api/MemoryIndividual/admin/participants?search=...&status=...&page=...&pageSize=...
 * 
 * @async
 * @function GetAdminParticipants
 * @param {Object} [filterParams={}] - { search, status, page, pageSize }
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetAdminParticipants = async (filterParams = {}) => {
    try {
        const params = new URLSearchParams();
        if (filterParams.search) params.append('search', filterParams.search);
        if (filterParams.status && filterParams.status !== 'all') params.append('status', filterParams.status);
        if (filterParams.page) params.append('page', filterParams.page);
        if (filterParams.pageSize) params.append('pageSize', filterParams.pageSize);

        const queryString = params.toString();
        const url = `${API_URL}MemoryIndividual/admin/participants${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al obtener participantes de la dinámica.'
            };
        }
        return resData.success !== undefined
            ? resData
            : { success: true, data: resData.data !== undefined ? resData.data : resData };
    } catch (error) {
        console.error("Error en GetAdminParticipants (DL):", error);
        return { success: false, message: 'Error de conexión al obtener participantes.' };
    }
};

/**
 * Descalifica a un aficionado con motivo justificado para auditoría.
 * POST /api/MemoryIndividual/admin/participants/{id}/disqualify
 * 
 * @async
 * @function DisqualifyParticipant
 * @param {number|string} id - ID del participante.
 * @param {Object} payload - { reason, adminUser }
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const DisqualifyParticipant = async (id, payload) => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/admin/participants/${id}/disqualify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al descalificar al aficionado.'
            };
        }
        return resData.success !== undefined ? resData : { success: true, message: 'Aficionado descalificado.' };
    } catch (error) {
        console.error("Error en DisqualifyParticipant (DL):", error);
        return { success: false, message: 'Error de conexión al descalificar aficionado.' };
    }
};

/**
 * Revierte la descalificación de un aficionado.
 * POST /api/MemoryIndividual/admin/participants/{id}/undisqualify
 * 
 * @async
 * @function UndisqualifyParticipant
 * @param {number|string} id - ID del participante.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const UndisqualifyParticipant = async (id) => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/admin/participants/${id}/undisqualify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al restituir al aficionado.'
            };
        }
        return resData.success !== undefined ? resData : { success: true, message: 'Aficionado restituido.' };
    } catch (error) {
        console.error("Error en UndisqualifyParticipant (DL):", error);
        return { success: false, message: 'Error de conexión al restituir aficionado.' };
    }
};

/**
 * Establece o revoca el estatus de ganador de un aficionado.
 * POST /api/MemoryIndividual/admin/participants/{id}/winner
 * 
 * @async
 * @function SetParticipantWinner
 * @param {number|string} id - ID del participante.
 * @param {Object} payload - { isWinner, winnerRank }
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const SetParticipantWinner = async (id, payload) => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/admin/participants/${id}/winner`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al actualizar estatus de ganador.'
            };
        }
        return resData.success !== undefined ? resData : { success: true, message: 'Estatus de ganador actualizado.' };
    } catch (error) {
        console.error("Error en SetParticipantWinner (DL):", error);
        return { success: false, message: 'Error de conexión al actualizar estatus de ganador.' };
    }
};

/**
 * Activa o pausa la dinámica de Memorama Individual.
 * POST /api/MemoryIndividual/admin/toggle-status
 * 
 * @async
 * @function ToggleAdminDynamicStatus
 * @param {Object} payload - { isActive }
 * @returns {Promise<{success: boolean, isActive?: boolean, message?: string}>}
 */
export const ToggleAdminDynamicStatus = async (payload) => {
    try {
        const bodyData = typeof payload === 'object' && payload !== null
            ? { isActive: Boolean(payload.isActive) }
            : { isActive: Boolean(payload) };

        const response = await fetch(`${API_URL}MemoryIndividual/admin/toggle-status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(bodyData)
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al alternar estatus de la dinámica.'
            };
        }
        return resData.success !== undefined ? resData : { success: true, isActive: bodyData.isActive, message: 'Estatus de dinámica actualizado.' };
    } catch (error) {
        console.error("Error en ToggleAdminDynamicStatus (DL):", error);
        return { success: false, message: 'Error de conexión al alternar estatus de dinámica.' };
    }
};

/**
 * Reinicia la dinámica de Memorama Individual (limpia participantes y métricas de prueba previas al partido).
 * POST /api/MemoryIndividual/admin/reset
 * 
 * @async
 * @function ResetAdminDynamic
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const ResetAdminDynamic = async () => {
    try {
        const response = await fetch(`${API_URL}MemoryIndividual/admin/reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'Error al reiniciar la dinámica.'
            };
        }
        return resData.success !== undefined ? resData : { success: true, message: 'Dinámica reiniciada exitosamente.' };
    } catch (error) {
        console.error("Error en ResetAdminDynamic (DL):", error);
        return { success: false, message: 'Error de conexión al reiniciar la dinámica.' };
    }
};

/**
 * Retorna la URL directa para la exportación del reporte ejecutivo en Excel (.xlsx).
 * 
 * @function GetExportExcelUrl
 * @returns {string} URL del endpoint de exportación.
 */
export const GetExportExcelUrl = () => {
    return `${API_URL}MemoryIndividual/admin/export-excel`;
};

const DLMemoryIndividual = {
    GetPublicConfig,
    RegisterParticipant,
    StartGameSession,
    SubmitResult,
    GetLeaderboard,
    TrackMetric,
    GetAdminThemes,
    GetAdminThemeById,
    SaveAdminTheme,
    DeleteAdminTheme,
    SelectActiveTheme,
    UploadCardImage,
    SendQROverWebSocket,
    GetAdminStats,
    GetAdminParticipants,
    DisqualifyParticipant,
    UndisqualifyParticipant,
    SetParticipantWinner,
    ToggleAdminDynamicStatus,
    ResetAdminDynamic,
    GetExportExcelUrl
};

export default DLMemoryIndividual;
